// Central tracking + consent library.
// - Reads TrackingSettings from backend (public IDs only)
// - Google Consent Mode v2 default: all denied → updated after user consent
// - Loads GTM, GA4, Meta Pixel only when their category is granted
// - Attribution capture (utm_*, gclid, gbraid, wbraid, fbclid) → sessionStorage
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONSENT_KEY = "tn-consent-v2";
const ATTR_KEY = "tn-attribution";
const SESSION_KEY = "tn-session-id";

// --- Session & attribution ---
export const getSessionId = () => {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const ATTR_PARAMS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
  "gclid", "gbraid", "wbraid", "fbclid", "ttclid", "msclkid", "li_fat_id",
];

export const captureAttribution = () => {
  try {
    const url = new URL(window.location.href);
    const captured = {};
    ATTR_PARAMS.forEach((p) => {
      const v = url.searchParams.get(p);
      if (v) captured[p] = v;
    });
    if (Object.keys(captured).length === 0) return getAttribution();
    captured._first_touch_at = new Date().toISOString();
    captured._landing_page = window.location.pathname;
    captured._referrer = document.referrer || null;
    sessionStorage.setItem(ATTR_KEY, JSON.stringify(captured));
    return captured;
  } catch {
    return {};
  }
};

export const getAttribution = () => {
  try {
    const raw = sessionStorage.getItem(ATTR_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

// --- Consent ---
export const getConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const saveConsent = (choice) => {
  const record = {
    consent_id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    session_id: getSessionId(),
    necessary: true,
    statistics: !!choice.statistics,
    marketing: !!choice.marketing,
    external_media: !!choice.external_media,
    banner_version: "1.0",
    at: new Date().toISOString(),
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  // Fire and forget to backend
  axios.post(`${API}/consent`, record).catch(() => {});
  updateConsentMode(record);
  return record;
};

// --- Google Consent Mode v2 default (must run BEFORE any GTM load) ---
export const initConsentMode = () => {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  const current = getConsent();
  const defaults = {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  };
  window.gtag("consent", "default", defaults);
  if (current) updateConsentMode(current);
};

export const updateConsentMode = (c) => {
  if (!window.gtag) return;
  window.gtag("consent", "update", {
    ad_storage: c.marketing ? "granted" : "denied",
    ad_user_data: c.marketing ? "granted" : "denied",
    ad_personalization: c.marketing ? "granted" : "denied",
    analytics_storage: c.statistics ? "granted" : "denied",
  });
};

// --- Loaders (dynamic script injection) ---
const injected = new Set();

const injectScript = (id, src) => {
  if (injected.has(id) || document.getElementById(id)) return;
  injected.add(id);
  const s = document.createElement("script");
  s.id = id; s.async = true; s.src = src;
  document.head.appendChild(s);
};

export const loadTracking = async () => {
  try {
    const { data } = await axios.get(`${API}/tracking/public`);
    const c = getConsent();
    if (!c) return; // no consent yet – wait

    // GTM (statistics OR marketing)
    if (data.gtm_id && (c.statistics || c.marketing)) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
      injectScript("gtm-script", `https://www.googletagmanager.com/gtm.js?id=${data.gtm_id}`);
    }
    // GA4 (statistics)
    if (data.ga4_id && c.statistics) {
      injectScript("ga4-script", `https://www.googletagmanager.com/gtag/js?id=${data.ga4_id}`);
      window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      window.gtag("config", data.ga4_id, { anonymize_ip: true });
    }
    // Meta Pixel (marketing)
    if (data.meta_pixel_id && c.marketing && !window.fbq) {
      /* eslint-disable */
      !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
      /* eslint-enable */
      window.fbq("init", data.meta_pixel_id);
      window.fbq("track", "PageView");
    }
  } catch (e) {
    // silent – tracking never blocks UI
  }
};

// --- Unified event fire ---
export const trackEvent = (event, params = {}) => {
  const attribution = getAttribution();
  const meta = { ...params, ...attribution };
  // Backend log (always – rate-safe, non-blocking)
  axios.post(`${API}/track/event`, {
    event,
    session_id: getSessionId(),
    page: window.location.pathname,
    attribution,
    meta,
  }).catch(() => {});
  // GA4 / GTM
  if (window.gtag) window.gtag("event", event, meta);
  if (window.dataLayer) window.dataLayer.push({ event, ...meta });
  // Meta Pixel (common events mapping)
  if (window.fbq) {
    const map = {
      generate_lead: "Lead", form_start: "InitiateCheckout",
      click_whatsapp: "Contact", click_email: "Contact", click_phone: "Contact",
      view_content: "ViewContent", thank_you_page_view: "Lead",
    };
    const std = map[event];
    if (std) window.fbq("track", std, { event_id: params.event_id || `${event}_${Date.now()}` });
    else window.fbq("trackCustom", event, meta);
  }
};
